import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import {
  AppAdminFormLabel,
  AppButton,
  AppCheckbox,
  AppCol,
  AppRadio,
  AppRadioGroup,
  AppRow,
  AppSelect,
} from '@/components/atoms';
import { TextFieldWithLabel } from '@/components/molecules';
import { InsurerFormData } from '@/schemas/insurerFormSchema';
import { InsurerFormInitData } from '@/hooks/admin/useInsurerFormInitData';
import { useAddressSearchModalHelper } from '@/hooks/useAddressSearchModalHelper';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { fullWidthToHalfWidthNumber } from '@/utils';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import { dateValueToIso as parseDateValueToIso } from '@/utils/dateConverter';
import { Insurer } from '@/types/admin-insurer';
import AddressSearchModal from './AddressSearchModal';

interface InsurerFormProps {
  initData: InsurerFormInitData;
  isEdit?: boolean;
  insurer?: Insurer;
}

const dateValueToIso = (value: string | null | undefined): string => {
  return parseDateValueToIso(value) ?? '';
};

const InsurerForm = ({ initData, isEdit = false, insurer }: InsurerFormProps) => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const { control, watch, setValue, reset, clearErrors } = useFormContext<InsurerFormData>();
  const addressSearchMutation = useAddressSearch({
    siteType: currentSite,
    isAdmin: true,
  });

  // Helper: wrap field.onChange to clear error on change
  const withClearError = useCallback(
    <K extends keyof InsurerFormData>(
      name: K,
      field: ControllerRenderProps<InsurerFormData, K>,
    ) => ({
      ...field,
      onChange: (...args: Parameters<typeof field.onChange>) => {
        clearErrors(name);
        return field.onChange(...args);
      },
    }),
    [clearErrors],
  );

  // 保険者名称 → 保険者仮名 auto-fill (katakana)
  const insurerNameKana = String(watch('insurer_name_kana') ?? '');
  const nameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: insurerNameKana,
    setNameValue: v => setValue('insurer_name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v => setValue('insurer_name_kana', v, { shouldDirty: true, shouldValidate: false }),
    onClearNameError: () => clearErrors('insurer_name'),
    onClearKanaError: () => clearErrors('insurer_name_kana'),
    maxLength: Infinity,
  });

  useEffect(() => {
    if (!isEdit || !insurer) return;

    const insurerTypeId = insurer.insurer_type?.id;
    const validInsurerTypeId = initData.insurer_types.some(t => t.id === insurerTypeId)
      ? insurerTypeId!
      : initData.insurer_types[0]?.id ?? 0;

    const insuranceKindId = insurer.insurance_kind?.id;
    const validInsuranceKindId = initData.insurance_kinds.some(k => k.id === insuranceKindId)
      ? insuranceKindId!
      : initData.insurance_kinds[0]?.id ?? 0;

    const claimTypeId = insurer.medix_claim_type?.id;
    const validClaimTypeId = initData.claim_types.some(c => c.id === claimTypeId)
      ? claimTypeId!
      : initData.claim_types[0]?.id ?? 0;

    reset({
      insurer_number: insurer.insurer_number || '',
      sub_number: insurer.sub_number.toString() || '',
      insurer_type_id: validInsurerTypeId,
      insurance_kind_id: validInsuranceKindId,
      insurer_name_kana: insurer.insurer_name_kana || '',
      insurer_name: insurer.insurer_name || '',
      staff_department: insurer.staff_department || '',
      delegation_insurer_name: insurer.delegation_insurer_name || '',
      postal_code1: insurer.postal_code1 || '',
      postal_code2: insurer.postal_code2 || '',
      address1: insurer.address1 || '',
      address2: insurer.address2 || '',
      address3: insurer.address3 || '',
      telephone: insurer.telephone || '',
      available_from: insurer.available_from || '',
      available_to:
        !insurer.available_to || insurer.available_to === '2100-12-31'
          ? ''
          : insurer.available_to,
      cost_rate: insurer.cost_rate != null ? String(insurer.cost_rate) : '0',
      family_cost_rate: insurer.family_cost_rate != null ? String(insurer.family_cost_rate) : '0',
      is_special: insurer.is_special ? 1 : 0,
      is_enabled_invoice: insurer.is_enabled_invoice ? 1 : 0,
      is_nonoutput_rezept: insurer.is_nonoutput_rezept ? 1 : 0,
      is_partial_aid: insurer.is_partial_aid ? 1 : 0,
      medix_claim_type_id: validClaimTypeId,
      medix_visit_detail_report_id: insurer.medix_visit_detail_report?.id || 0,
      upsystem_visit_detail_report_id: insurer.upsystem_visit_detail_report?.id || 0,
      medix_attachement_document_id: insurer.medix_attachement_document?.id ?? undefined,
      upsystem_attachement_document_id: insurer.upsystem_attachement_document?.id ?? undefined,
      medix_visit_report_document_id: insurer.medix_visit_report_document?.id ?? undefined,
      upsystem_visit_report_document_id: insurer.upsystem_visit_report_document?.id ?? undefined,
      medix_visit_detail_report_delegation_id:
        insurer.medix_visit_detail_report_delegation?.id || 0,
      upsystem_visit_detail_report_delegation_id:
        insurer.upsystem_visit_detail_report_delegation?.id || 0,
      is_receipt_delegation: insurer.is_receipt_delegation ? 1 : 0,
      receipt_delegation_from: insurer.receipt_delegation_from || '',
      receipt_delegation_to: insurer.receipt_delegation_to || '',
      calc_type: insurer.special_calculation_cost?.calc_type ?? null,
      limit_amount: insurer.special_calculation_cost?.limit_amount != null
        ? String(insurer.special_calculation_cost.limit_amount) : '',
      limit_num_month: insurer.special_calculation_cost?.limit_num_month != null
        ? String(insurer.special_calculation_cost.limit_num_month) : '',
      limit_amount_month: insurer.special_calculation_cost?.limit_amount_month != null
        ? String(insurer.special_calculation_cost.limit_amount_month) : '',
      is_adopted_lower_cost: insurer.special_calculation_cost?.is_adopted_lower_cost ? 1 : 0,
    });
  }, [isEdit, insurer, reset, initData]);

  const postalCode1 = String(watch('postal_code1') ?? '');
  const postalCode2 = String(watch('postal_code2') ?? '');
  const address1 = String(watch('address1') ?? '');
  const combinedPostalCode = `${postalCode1}${postalCode2}`;
  const calcType = watch('calc_type');
  // FB No.109: 負担割合が0%でなく特殊医療助成が設定されていれば自動で ON（手動変更不可）。
  const isAidRateSpecialCalc = calcType != null && Number(watch('cost_rate') || 0) !== 0;
  useEffect(() => {
    setValue('is_adopted_lower_cost', isAidRateSpecialCalc ? 1 : 0);
  }, [isAidRateSpecialCalc, setValue]);

  const {
    isAddressModalOpen,
    closeAddressModal,
    handleAddressButtonClick,
    handleAddressSelect,
    modalInput,
  } = useAddressSearchModalHelper<InsurerFormData>({
    postalCodeValue: combinedPostalCode,
    addressValue: address1,
    setValue,
    fieldNames: {
      postalCode1: 'postal_code1',
      postalCode2: 'postal_code2',
      address: 'address1',
    },
    addressSearchMutation,
  });

  return (
    <div className="space-y-6">
      {/* 保険者番号 */}
      <AppRow gutter={[16, 16]} align="top">
        <AppCol xs={24} sm={12}>
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
            <Controller
              name="insurer_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextFieldWithLabel
                  {...withClearError('insurer_number', field)}
                  label="保険者番号"
                  placeholder=""
                  required
                  error={error}
                />
              )}
            />
            <div className="pt-10 px-1">
              <span className="text-gray-500">-</span>
            </div>
            <div className="pt-5">
              <Controller
                name="sub_number"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextFieldWithLabel {...withClearError('sub_number', field)} label="" placeholder="" error={error} />
                )}
              />
            </div>
          </div>
        </AppCol>
      </AppRow>

      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <div>
            <AppAdminFormLabel label="保険種別" />
            <Controller
              name="insurer_type_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <AppSelect
                    {...field}
                    value={field.value || undefined}
                    status={error ? 'error' : undefined}
                    placeholder=""
                    options={initData.insurer_types.map(item => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={v => { clearErrors('insurer_type_id'); field.onChange(v); }}
                    className="w-full"
                    defaultValue={initData.insurer_types[0]?.name}
                  />
                  <div className="h-6 mt-1">
                    {error && <p className="text-red-500 text-sm">{error.message}</p>}
                  </div>
                </>
              )}
            />
          </div>
        </AppCol>
        <AppCol xs={24} sm={12}>
          <div>
            <AppAdminFormLabel label="保険制度" />
            <Controller
              name="insurance_kind_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <AppSelect
                    {...field}
                    value={field.value || undefined}
                    status={error ? 'error' : undefined}
                    placeholder=""
                    options={initData.insurance_kinds.map(item => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={v => { clearErrors('insurance_kind_id'); field.onChange(v); }}
                    className="w-full"
                    defaultValue={initData.insurance_kinds[0]?.name}
                  />
                  <div className="h-6 mt-1">
                    {error && <p className="text-red-500 text-sm">{error.message}</p>}
                  </div>
                </>
              )}
            />
          </div>
        </AppCol>
      </AppRow>

      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <Controller
            name="insurer_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...field}
                {...nameKanaFields.nameInputProps}
                label="保険者名称"
                required
                error={error}
              />
            )}
          />
        </AppCol>
        <AppCol xs={24} sm={12}>
          <Controller
            name="insurer_name_kana"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...field}
                {...nameKanaFields.kanaInputProps}
                label="保険者仮名"
                error={error}
              />
            )}
          />
        </AppCol>
      </AppRow>

      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <Controller
            name="staff_department"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel {...withClearError('staff_department', field)} label="部署名" error={error} />
            )}
          />
        </AppCol>
        <AppCol xs={24} sm={12}>
          <Controller
            name="delegation_insurer_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel {...withClearError('delegation_insurer_name', field)} label="受領委任払い用名称" error={error} />
            )}
          />
        </AppCol>
      </AppRow>

      <AppRow gutter={[16, 16]} align="top">
        <AppCol xs={24} sm={12}>
          <AppAdminFormLabel required label="郵便番号" />
          <div className="mt-1 grid items-start grid-cols-[1fr_auto_1fr] gap-2">
            <Controller
              name="postal_code1"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <TextFieldWithLabel
                    {...withClearError('postal_code1', field)}
                    onChange={e => {
                      clearErrors('postal_code1');
                      field.onChange(fullWidthToHalfWidthNumber(e.target.value));
                    }}
                    label=""
                    placeholder=""
                    maxLength={3}
                    error={error}
                  />
                </div>
              )}
            />
            <div className="pt-4 px-1">
              <span className="text-gray-500">-</span>
            </div>
            <Controller
              name="postal_code2"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <TextFieldWithLabel
                    {...withClearError('postal_code2', field)}
                    onChange={e => {
                      clearErrors('postal_code2');
                      field.onChange(fullWidthToHalfWidthNumber(e.target.value));
                    }}
                    label=""
                    placeholder=""
                    maxLength={4}
                    error={error}
                  />
                </div>
              )}
            />
          </div>
        </AppCol>
        <AppCol xs={24} sm={12}>
          <div className="pt-9">
            <AppButton type="primary" htmlType="button" onClick={handleAddressButtonClick}>
              住所補助
            </AppButton>
          </div>
        </AppCol>
      </AppRow>

      <div>
        <div className="mt-1 grid grid-cols-3 items-end gap-3">
          <Controller
            name="address1"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...withClearError('address1', field)}
                label="住所"
                required
                placeholder=""
                value={field.value ?? ''}
                error={error}
              />
            )}
          />
          <Controller
            name="address2"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...withClearError('address2', field)}
                label=""
                required
                placeholder=""
                value={field.value ?? ''}
                error={error}
              />
            )}
          />
          <Controller
            name="address3"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...withClearError('address3', field)}
                label=""
                placeholder="建物名・部屋番号"
                value={field.value ?? ''}
                error={error}
              />
            )}
          />
        </div>
      </div>

      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <Controller
            name="telephone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel {...withClearError('telephone', field)} label="電話番号" error={error} />
            )}
          />
        </AppCol>
      </AppRow>

      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <AppAdminFormLabel required label="適用期間" />
          <div className="mt-1 grid grid-cols-[1fr_auto_1fr] gap-3">
            <Controller
              name="available_from"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={value => { clearErrors('available_from'); field.onChange(dateValueToIso(value)); }}
                    placeholder=""
                    className="w-full"
                    error={!!error}
                  />
                  <div className="h-6 mt-1">
                    {error && <p className="text-red-500 text-sm">{error.message}</p>}
                  </div>
                </div>
              )}
            />
            <div className="pt-2">-</div>
            <Controller
              name="available_to"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={value => { clearErrors('available_to'); field.onChange(dateValueToIso(value)); }}
                    placeholder=""
                    className="w-full"
                    error={!!error}
                  />
                  <div className="h-6 mt-1">
                    {error && <p className="text-red-500 text-sm">{error.message}</p>}
                  </div>
                </div>
              )}
            />
          </div>
        </AppCol>
        <AppCol xs={24} sm={6}>
          <Controller
            name="cost_rate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...field}
                onChange={e => {
                  clearErrors('cost_rate');
                  const converted = fullWidthToHalfWidthNumber((e.target as HTMLInputElement).value);
                  field.onChange(converted);
                  setValue('family_cost_rate', converted, { shouldDirty: true, shouldValidate: false });
                }}
                onBlur={() => {
                  const val = String(field.value ?? '');
                  setValue('family_cost_rate', val, { shouldDirty: true, shouldValidate: false });
                }}
                label="本人負担"
                placeholder=""
                className="w-full"
                error={error}
              />
            )}
          />
        </AppCol>
        <AppCol xs={24} sm={6}>
          <Controller
            name="family_cost_rate"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextFieldWithLabel
                {...withClearError('family_cost_rate', field)}
                label="家族負担"
                placeholder=""
                className="w-full"
                disabled
                error={error}
              />
            )}
          />
        </AppCol>
      </AppRow>

      {/* 請求区分 */}
      <AppRow gutter={[16, 16]}>
        <AppCol xs={24} sm={12}>
          <div>
            <AppAdminFormLabel label="請求区分" />
            <Controller
              name="medix_claim_type_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <AppSelect
                    {...field}
                    value={field.value || undefined}
                    status={error ? 'error' : undefined}
                    placeholder=""
                    options={initData.claim_types.map(item => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={v => { clearErrors('medix_claim_type_id'); field.onChange(v); }}
                    className="w-full"
                    defaultValue={initData.claim_types[0]?.name}
                  />
                  <div className="h-6 mt-1">
                    {error && <p className="text-red-500 text-sm">{error.message}</p>}
                  </div>
                </>
              )}
            />
          </div>
        </AppCol>
      </AppRow>

      {/* 特殊医療助成金 */}
      <div className="pt-6">
        <h3 className="text-lg font-semibold mb-4 border-b pb-2">特殊医療助成金</h3>

        <AppRow gutter={[16, 16]}>
          <AppCol xs={24}>
            <div>
              <AppAdminFormLabel label="計算種別" />
              <Controller
                name="calc_type"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <AppRadioGroup
                      value={field.value}
                      onChange={e => {
                        clearErrors('calc_type');
                        const val = e.target.value as number | null;
                        field.onChange(val);
                        if (val === 1) {
                          setValue('limit_amount_month', '');
                        } else if (val === 2) {
                          setValue('limit_amount', '');
                          setValue('limit_num_month', '');
                        } else if (val === 3) {
                          setValue('limit_num_month', '');
                        } else {
                          setValue('limit_amount', '');
                          setValue('limit_num_month', '');
                          setValue('limit_amount_month', '');
                        }
                      }}
                      className="flex gap-8"
                    >
                      <AppRadio value={null}>設定なし</AppRadio>
                      <AppRadio value={1}>1回〇〇円　月●回まで</AppRadio>
                      <AppRadio value={2}>1ヵ月上限（限度額）〇〇円</AppRadio>
                      <AppRadio value={3}>1回〇〇円　までで1ヵ月上限（限度額）〇〇円</AppRadio>
                    </AppRadioGroup>
                    <div className="h-6 mt-1">
                      {error && <p className="text-red-500 text-sm">{error.message}</p>}
                    </div>
                  </>
                )}
              />
            </div>
          </AppCol>
        </AppRow>

        <AppRow gutter={[16, 16]} align="bottom">
          <AppCol xs={24} sm={6}>
            <Controller
              name="limit_amount"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextFieldWithLabel
                  {...withClearError('limit_amount', field)}
                  onChange={e => {
                    clearErrors('limit_amount');
                    field.onChange(fullWidthToHalfWidthNumber(e.target.value));
                  }}
                  label="負担金"
                  placeholder=""
                  disabled={calcType !== 1 && calcType !== 3}
                  error={error}
                />
              )}
            />
          </AppCol>
          <AppCol xs={24} sm={6}>
            <Controller
              name="limit_num_month"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextFieldWithLabel
                  {...withClearError('limit_num_month', field)}
                  onChange={e => {
                    clearErrors('limit_num_month');
                    field.onChange(fullWidthToHalfWidthNumber(e.target.value));
                  }}
                  label="月間上限回数"
                  placeholder=""
                  disabled={calcType !== 1}
                  error={error}
                />
              )}
            />
          </AppCol>
          <AppCol xs={24} sm={6}>
            <Controller
              name="limit_amount_month"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextFieldWithLabel
                  {...withClearError('limit_amount_month', field)}
                  onChange={e => {
                    clearErrors('limit_amount_month');
                    field.onChange(fullWidthToHalfWidthNumber(e.target.value));
                  }}
                  label="月間上限金額"
                  placeholder=""
                  disabled={calcType !== 2 && calcType !== 3}
                  error={error}
                />
              )}
            />
          </AppCol>
          <AppCol xs={24} sm={6} className="pb-7">
            <Controller
              name="is_adopted_lower_cost"
              control={control}
              render={() => (
                <AppCheckbox checked={isAidRateSpecialCalc} disabled>
                  医療助成の負担割合で特殊医療助成の計算をする
                </AppCheckbox>
              )}
            />
          </AppCol>
        </AppRow>
      </div>

      <AddressSearchModal
        isOpen={isAddressModalOpen}
        onClose={closeAddressModal}
        onSelect={handleAddressSelect}
        initialPostalCode1={modalInput.initialPostalCode1}
        initialPostalCode2={modalInput.initialPostalCode2}
        initialAddress={modalInput.initialAddress}
        title="住所補助"
        isAdmin={true}
        siteType={currentSite}
      />
    </div>
  );
};

export default InsurerForm;
